import React, { useRef, useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

export interface TailwindInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  error?: string;
  icon?: string; // Material Symbols icon name
  iconPosition?: 'left' | 'right';
  suffix?: string; // Text suffix (e.g., "kg", "$", "%")
  prefix?: string; // Text prefix
  fullWidth?: boolean;
}

export default function TailwindInput({
  label,
  helperText,
  error,
  icon,
  iconPosition = 'left',
  suffix,
  prefix,
  fullWidth = false,
  className,
  disabled,
  ...props
}: TailwindInputProps) {
  const prefixRef = useRef<HTMLSpanElement>(null);
  const suffixRef = useRef<HTMLSpanElement>(null);
  const [prefixWidth, setPrefixWidth] = useState(0);
  const [suffixWidth, setSuffixWidth] = useState(0);

  useEffect(() => {
    if (prefixRef.current) {
      setPrefixWidth(prefixRef.current.offsetWidth);
    }
  }, [prefix]);

  useEffect(() => {
    if (suffixRef.current) {
      setSuffixWidth(suffixRef.current.offsetWidth);
    }
  }, [suffix]);

  const dynamicPaddingLeft = prefix && !icon ? prefixWidth + 16 : undefined;
  const dynamicPaddingRight = suffix && !icon ? suffixWidth + 16 : undefined;

  const inputClasses = cn(
    'appearance-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-slate-50 border text-slate-900 text-sm rounded-lg px-4 py-2.5 transition-colors',
    'focus:ring-primary focus:border-primary focus:outline-none',
    'placeholder:text-slate-400',
    error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-slate-200',
    disabled && 'opacity-50 cursor-not-allowed bg-slate-50',
    icon && iconPosition === 'left' && 'pl-10',
    icon && iconPosition === 'right' && 'pr-10',
    fullWidth ? 'w-full' : '',
    className
  );

  return (
    <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
      {label && (
        <label className="text-sm font-medium text-slate-700 px-1">
          {label}
        </label>
      )}

      <div className="relative">
        {/* Left side: icon or prefix */}
        {icon && iconPosition === 'left' && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <span className="material-symbols-outlined text-[20px] text-slate-400">
              {icon}
            </span>
          </div>
        )}
        {prefix && !icon && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <span ref={prefixRef} className="text-sm text-slate-500 whitespace-nowrap">{prefix}</span>
          </div>
        )}

        <input
          className={inputClasses}
          disabled={disabled}
          style={{
            ...(dynamicPaddingLeft ? { paddingLeft: dynamicPaddingLeft } : {}),
            ...(dynamicPaddingRight ? { paddingRight: dynamicPaddingRight } : {}),
          }}
          onWheel={(e) => e.currentTarget.blur()}
          {...props}
        />

        {/* Right side: icon or suffix */}
        {icon && iconPosition === 'right' && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <span className="material-symbols-outlined text-[20px] text-slate-400">
              {icon}
            </span>
          </div>
        )}
        {suffix && !icon && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <span ref={suffixRef} className="text-sm text-slate-500 whitespace-nowrap">{suffix}</span>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-1 mt-0.5 px-1.5 py-1 bg-red-50 border border-red-200 rounded text-xs text-red-700">
          <span className="material-symbols-outlined text-[14px]">error</span>
          {error}
        </div>
      )}

      {helperText && !error && (
        <p className="text-xs text-slate-500 px-1">{helperText}</p>
      )}
    </div>
  );
}
